import { Button } from '@/components/ui/button';
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
} from '@/components/ui/menu';
import { FiChevronDown, FiCopy } from 'react-icons/fi';
import { FaDatabase } from 'react-icons/fa';
import {
  AccordionItem,
  Accordion,
  HStack,
  Icon,
  Text,
  VStack,
  Image,
} from '@chakra-ui/react';
import {
  AccordionItemContent,
  AccordionItemTrigger,
  AccordionRoot,
} from '@/components/ui/accordion';
import { CiWallet } from 'react-icons/ci';
import { EthIcon } from '../ui/svgs/eth';
import { MessagesParams } from '@/i18n/settings';
import { useTranslations } from 'next-intl';
import { useSelector } from 'react-redux';
import {
  reduxWalletsList,
  setWalletId,
  userWalletsItem,
  WalletListItem,
  reduxWalletsId,
} from '@/lib/stores/walletSlice';
import { requestSetWalletInfo } from '@/lib/services/wallet.service';
import { marketCapValueFormat, formatPrice } from '@/lib/utils/number.utils';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '@/lib/hooks/storeHook';
import { useQueryClient } from '@tanstack/react-query';
import { CHAIN_INFO, CHAIN_ID_MAP } from '@/lib/config/chain';
import { toaster } from '../ui/toaster';

interface UserWalletMenuProps {
  address: string;
}

export function UserWalletMenu() {
  const dispatch = useAppDispatch();
  const walletsList = useSelector(reduxWalletsList) || [];
  const walletId = useSelector(reduxWalletsId);
  const queryClient = useQueryClient();
  const tAddress = useTranslations('TradePage.TokenDetail');

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    //toast message
    toaster.create({
      title: tAddress('addressCopied'),
      type: 'success',
    });
  };

  const [defaultWallet, setDefaultWallet] = useState<WalletListItem>();

  const t = useTranslations('Navigation.userWalletMenu');

  useEffect(() => {
    if (walletsList.length > 0) {
      let data: WalletListItem[] = [];
      data = walletsList.filter((item: WalletListItem) => {
        return item.walletGroupId == walletId;
      });
      if (data.length > 0) {
        setDefaultWallet(data[0]);
      }
    }
  }, [walletsList, walletId]);

  const onSetDefaultWallet = async (data: WalletListItem) => {
    let res = await requestSetWalletInfo({
      wallet_group_id: data?.walletGroupId,
    });
    if (res.code == 10000) {
      dispatch(setWalletId(data?.walletGroupId || ''));
      setDefaultWallet(data);
      queryClient.invalidateQueries({ queryKey: ['user_currency_list'] });
    }
  };

  return (
    <MenuRoot>
      <MenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          bg="bg.t2"
          borderRadius="100px"
          px={4}
          py={2}
          borderColor="text.t4"
          _hover={{
            bg: 'bg.t1',
          }}
        >
          <HStack gap={2}>
            <Icon fontSize={20}>
              <CiWallet />
            </Icon>
            <Text color="text.t1" fontSize="sm" fontWeight="medium">
              {t('wallet')} {defaultWallet?.walletGroupId}
            </Text>
            {/* <Icon>
              <EthIcon width={5} />
            </Icon> */}
            {/* <Image
              rounded="full"
              src="/images/Solana.png"
              w={'18px'}
              h={'18px'}
            /> */}
            <Text color="text.t2" fontSize="sm">
              $
              {defaultWallet?.wallGroupValues
                ? Number(defaultWallet?.wallGroupValues).toFixed(2)
                : '0.00'}
            </Text>
            <FiChevronDown color="text.t3" size={16} />
          </HStack>
        </Button>
      </MenuTrigger>

      <MenuContent
        width={320}
        padding={4}
        gap={4}
        borderRadius="12px"
        bg="bg.t1"
        backdropFilter="blur(20px)"
      >
        <AccordionRoot>
          {walletsList.map((item: WalletListItem, index) => (
            <AccordionItem key={index} value={item.walletGroupId.toString()}>
              <AccordionItemTrigger>
                <HStack
                  h={'44px'}
                  cursor={'pointer'}
                  onClick={() => {
                    onSetDefaultWallet(item);
                  }}
                >
                  <Icon>
                    <CiWallet />
                  </Icon>
                  <Text>
                    {t('wallet')} {item.walletGroupId}
                  </Text>
                </HStack>
              </AccordionItemTrigger>
              <AccordionItemContent maxH={'400px'}>
                {item?.userWallets.map((json: userWalletsItem, key: number) => (
                  <HStack
                    mt={3}
                    borderRadius="8px"
                    bg="bg.t2"
                    w="full"
                    justify="space-between"
                    align="stretch"
                    gap={3}
                    p={2}
                    key={key}
                  >
                    <HStack justify="space-between">
                      {/* <Icon fontSize="20px">
                          <EthIcon width={6} />
                        </Icon> */}
                      <Image
                        src={CHAIN_INFO[CHAIN_ID_MAP[json.chainId]].icon}
                        rounded={'full'}
                        w={'18px'}
                        h={'18px'}
                      />
                      <VStack gap={0}>
                        <Text fontSize="sm">
                          {CHAIN_INFO[CHAIN_ID_MAP[json.chainId]].name}
                        </Text>
                        <Text fontSize="sm" color="text.t2">
                          ${json?.tokenValue.toFixed(2)}
                        </Text>
                      </VStack>
                    </HStack>
                    <HStack justify="space-between">
                      <HStack gap={2}>
                        <Text fontSize="sm" color="text.t2" fontWeight="medium">
                          {`${json?.userWalletAddress.slice(0, 4)}...${json?.userWalletAddress.slice(-4)}`}
                        </Text>
                      </HStack>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          handleCopyAddress(json?.userWalletAddress);
                        }}
                      >
                        <FiCopy />
                      </Button>
                    </HStack>
                  </HStack>
                ))}
              </AccordionItemContent>
            </AccordionItem>
          ))}
        </AccordionRoot>
      </MenuContent>
    </MenuRoot>
  );
}
